import { useMemo } from "react"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createExposureMultiDateQuery, createExposureIntradayBatchTrendQueries } from "gatherers/multi-date/exposure"
import { createRiskMultiDateQuery, createRiskIntradayBatchTrendQueries } from "gatherers/multi-date/risk"
import { createContentSetExposureMultiDateQuery, createContentSetExposureIntradayBatchTrendQueries } from "gatherers/multi-date/content-set-exposure"
import { createFactorSensitivityMultiDateQuery, createFactorSensitivityIntradayBatchTrendQueries } from "gatherers/multi-date/factor-sensitivity"
import { createPerformanceCumulativeMultiDateQuery } from "gatherers/multi-date/cumulative-performance"
import { transformPerformanceData, createFactorItems } from "connected-components/graph/hooks/use-performance-items"
import { getPerformanceApiStartDate } from "helpers/dates"
import { DEFAULT_THRESHOLD_PARAMS } from "helpers/threshold"
import { combineResultsTolerantly, isIntradaySimulationEligible } from "gatherers/helpers"
import { extractDatesFromFirstSeries, extractDatesFromEachSeries } from "connected-components/graph/helpers"
import { numberFormatter } from "helpers/formatters/numbers"
import { ModelMetadata } from "resources/models"
import { OK, ERROR, LOADING } from "helpers/data-statuses"
import { useIntradayBatchTrendMode, getIntradayChartMode } from "connected-components/dashboard/hooks/use-intraday-state"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"
import { ContentSetMetadata } from "../content-set/types"
import { useGraphData } from "../../hooks/use-graph-data"
import type { CustomGraphFamilyContext } from "./family"
import { buildAllFactorItems, buildAllContentSetFactorItems, CustomGraphItemDescriptor } from "./helpers"
import { useCustomGraphSegmentInputs } from "./use-custom-graph-segment-inputs"
import { useCustomGraphContentSetMetadata } from "./use-custom-graph-content-set-metadata"

interface CustomGraphComponentProps {
  modelMetadata: ModelMetadata
  familyContext: CustomGraphFamilyContext
  selectedMetrics: string[]
}

const NOTHING_SELECTED_MESSAGE = "No metrics selected. Use the metric selector to add one."
const SELECTED_METRICS_UNRESOLVED_MESSAGE = "Your selected metrics are no longer available in this model. Use the metric selector to choose new ones."

// Distinguishes "nothing selected" from "saved picks no longer resolve" (e.g. a removed factor id) — both would otherwise show the same empty chart.
function getMetricsEmptyMessage(selectedMetrics: string[], currentItems: CustomGraphItemDescriptor[]): string {
  if (selectedMetrics.length > 0 && currentItems.length === 0) {
    return SELECTED_METRICS_UNRESOLVED_MESSAGE
  }
  return NOTHING_SELECTED_MESSAGE
}

interface FamilyQueryArgs {
  modelId: string
  instrumentWithSegment: unknown
  baseWithSegment: unknown
  startDate: string
  endDate: string
  isIntradayMode: boolean
  intradayBatchId?: string | null
  lastIntradayBatchId?: string | null
}

interface FamilyBatchTrendArgs {
  modelId: string
  instrumentWithSegment: unknown
  baseWithSegment: unknown
  completedBatches: unknown[]
}

function getFamilyQuerySpec(familyContext: Exclude<CustomGraphFamilyContext, { family: "performance" }>) {
  switch (familyContext.family) {
    case "exposure":
      return {
        createLiveQuery: (args: FamilyQueryArgs) => createExposureMultiDateQuery(
          args.modelId, args.instrumentWithSegment, args.baseWithSegment, args.startDate, args.endDate, args.isIntradayMode,
          { batchId: args.intradayBatchId, lastIntradayBatchId: args.lastIntradayBatchId },
        ),
        createBatchTrendQueries: (args: FamilyBatchTrendArgs) => createExposureIntradayBatchTrendQueries(args.modelId, args.instrumentWithSegment, args.baseWithSegment, args.completedBatches),
        noDataMessage: "Intraday data isn't available yet.",
        thresholdParams: undefined,
        formatter: numberFormatter,
      }
    case "risk":
      return {
        createLiveQuery: (args: FamilyQueryArgs) => createRiskMultiDateQuery(
          args.modelId, args.instrumentWithSegment, args.baseWithSegment, args.startDate, args.endDate, args.isIntradayMode,
          { batchId: args.intradayBatchId, lastIntradayBatchId: args.lastIntradayBatchId },
        ),
        createBatchTrendQueries: (args: FamilyBatchTrendArgs) => createRiskIntradayBatchTrendQueries(args.modelId, args.instrumentWithSegment, args.baseWithSegment, args.completedBatches),
        noDataMessage: "Intraday risk data isn't available yet.",
        thresholdParams: undefined,
        formatter: undefined,
      }
    case "factor-sensitivity":
      return {
        createLiveQuery: (args: FamilyQueryArgs) => createFactorSensitivityMultiDateQuery(
          args.modelId, args.instrumentWithSegment, args.baseWithSegment, args.startDate, args.endDate, familyContext.sensitivityType, args.isIntradayMode,
          { batchId: args.intradayBatchId, lastIntradayBatchId: args.lastIntradayBatchId },
        ),
        createBatchTrendQueries: (args: FamilyBatchTrendArgs) => createFactorSensitivityIntradayBatchTrendQueries(args.modelId, args.instrumentWithSegment, args.baseWithSegment, args.completedBatches, familyContext.sensitivityType),
        noDataMessage: "Intraday factor sensitivity data isn't available yet.",
        thresholdParams: DEFAULT_THRESHOLD_PARAMS,
        formatter: undefined,
      }
    case "content-set":
      return {
        createLiveQuery: (args: FamilyQueryArgs) => createContentSetExposureMultiDateQuery(
          args.modelId, familyContext.contentSetId, args.instrumentWithSegment, args.baseWithSegment, args.startDate, args.endDate, args.isIntradayMode,
          { batchId: args.intradayBatchId, lastIntradayBatchId: args.lastIntradayBatchId },
        ),
        createBatchTrendQueries: (args: FamilyBatchTrendArgs) => createContentSetExposureIntradayBatchTrendQueries(args.modelId, familyContext.contentSetId, args.instrumentWithSegment, args.baseWithSegment, args.completedBatches),
        noDataMessage: "Intraday content set data isn't available yet.",
        thresholdParams: undefined,
        formatter: numberFormatter,
      }
  }
}

function CustomBatchTrendGraph({ modelMetadata, familyContext, currentItems, contentSetMetadata, emptyMessage }: {
  modelMetadata: ModelMetadata
  familyContext: Exclude<CustomGraphFamilyContext, { family: "performance" }>
  currentItems: CustomGraphItemDescriptor[]
  contentSetMetadata?: ContentSetMetadata | null
  emptyMessage: string
}) {
  const {
    startDate,
    endDate,
    base,
    filter,
    normalization,
    instrument,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isIntradayMode,
  } = useGraphData()

  const { baseWithSegment, instrumentWithSegment, filterItems, intradayBatchId, lastIntradayBatchId } = useCustomGraphSegmentInputs({
    base, filter, normalization, instrument, disabledItems, intradayBatchMetadata,
  })

  const { isBatchTrendMode, completedBatches, isTruncated } = useIntradayBatchTrendMode(startDate, endDate, isIntradayMode, lastIntradayBatchId, intradayBatchId)

  const spec = useMemo(() => getFamilyQuerySpec(familyContext), [familyContext])

  const gatherers = useMemo(() => {
    const liveQuery = spec.createLiveQuery({
      modelId: modelMetadata.id, instrumentWithSegment, baseWithSegment, startDate, endDate, isIntradayMode,
      intradayBatchId, lastIntradayBatchId,
    })
    if (!isBatchTrendMode) return [liveQuery]
    return [liveQuery, ...spec.createBatchTrendQueries({ modelId: modelMetadata.id, instrumentWithSegment, baseWithSegment, completedBatches })]
  }, [spec, isBatchTrendMode, completedBatches, modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate, isIntradayMode, intradayBatchId, lastIntradayBatchId])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId)

  const intradayChartMode = getIntradayChartMode(isBatchTrendMode, isIntradaySimulationEligible(isIntradayMode, startDate, endDate, intradayBatchId, lastIntradayBatchId))
  const isOneDayMode = intradayChartMode !== "none"

  if (familyContext.family === "content-set" && contentSetMetadata === null) {
    return <MultiLineGraph
      status={OK}
      data={[]}
      startDate={startDate}
      endDate={endDate}
      items={[]}
      loadingMessage=""
      emptyMessage="This view's content set is no longer available."
    />
  }

  return <DataIntegrator
    dataTransform={isBatchTrendMode ? extractDatesFromEachSeries : extractDatesFromFirstSeries}
    combine={isBatchTrendMode ? combineResultsTolerantly : undefined}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      formatter: spec.formatter,
      thresholdParams: spec.thresholdParams,
      loadingMessage: "Loading data...",
      errorMessage: "Failed to load data. Please try again.",
      emptyMessage,
      noDataMessage: isOneDayMode ? spec.noDataMessage : undefined,
      isOneDayMode,
      intradayChartMode,
      isTruncated,
    }}
  />
}

function CustomPerformanceGraph({ modelMetadata, currentItems, emptyMessage }: {
  modelMetadata: ModelMetadata
  currentItems: CustomGraphItemDescriptor[]
  emptyMessage: string
}) {
  const {
    startDate,
    performanceStartDate,
    performanceEndDate,
    base,
    filter,
    normalization,
    instrument,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isIntradayMode,
    period,
    latestDate,
    dateOverride,
    isDateOverrideActive,
  } = useGraphData()

  const { baseWithSegment, instrumentWithSegment, filterItems, intradayBatchId, lastIntradayBatchId } = useCustomGraphSegmentInputs({
    base, filter, normalization, instrument, disabledItems, intradayBatchMetadata,
  })

  const effectivePeriod = isDateOverrideActive && dateOverride ? dateOverride.period : period
  const apiStartDate = isIntradayMode
    ? getPerformanceApiStartDate({ period: effectivePeriod, isIntradayMode, latestDate, performanceStartDate, performanceEndDate })
    : performanceStartDate

  const gatherers = useMemo(() => [createPerformanceCumulativeMultiDateQuery(
    modelMetadata.id, instrumentWithSegment, baseWithSegment, apiStartDate, performanceEndDate, isIntradayMode,
    { batchId: intradayBatchId, lastIntradayBatchId },
  )], [modelMetadata, instrumentWithSegment, baseWithSegment, apiStartDate, performanceEndDate, isIntradayMode, intradayBatchId, lastIntradayBatchId])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, apiStartDate, performanceEndDate, intradayPricingAsOf, lastIntradayBatchId)

  const isUsingIntradayQuery = isIntradaySimulationEligible(isIntradayMode, apiStartDate, performanceEndDate, intradayBatchId, lastIntradayBatchId)
  const intradayChartMode = getIntradayChartMode(false, isUsingIntradayQuery)

  return <DataIntegrator
    dataTransform={(data: unknown[]) => transformPerformanceData(data, modelMetadata.categories)}
    combine={undefined}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate: performanceEndDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      loadingMessage: "Loading data...",
      errorMessage: "Failed to load data. Please try again.",
      emptyMessage,
      noDataMessage: isUsingIntradayQuery ? "Intraday performance data isn't available yet." : undefined,
      isOneDayMode: isUsingIntradayQuery,
      intradayChartMode,
    }}
  />
}

export default function CustomGraphComponent({ modelMetadata, familyContext, selectedMetrics }: CustomGraphComponentProps) {
  const { startDate, endDate } = useGraphData()
  const { contentSetMetadata, contentSetsStatus } = useCustomGraphContentSetMetadata(familyContext)

  const allItems: CustomGraphItemDescriptor[] = useMemo(() => {
    if (familyContext.family === "content-set") return buildAllContentSetFactorItems(contentSetMetadata)
    if (familyContext.family === "performance") {
      return createFactorItems(modelMetadata?.factors).map(item => ({ id: item.id, label: item.label, category: item.category, type: "factor" as const }))
    }
    return buildAllFactorItems(modelMetadata)
  }, [familyContext, modelMetadata, contentSetMetadata])

  const currentItems: CustomGraphItemDescriptor[] = useMemo(() => {
    if (!selectedMetrics?.length) return []
    const byId = new Map(allItems.map(item => [item.id, item]))
    return selectedMetrics.map(id => byId.get(id)).filter((item): item is CustomGraphItemDescriptor => item != null)
  }, [allItems, selectedMetrics])

  const emptyMessage = useMemo(() => getMetricsEmptyMessage(selectedMetrics, currentItems), [selectedMetrics, currentItems])

  if (familyContext.family === "content-set" && contentSetsStatus === LOADING) {
    return <MultiLineGraph status={LOADING} startDate={startDate} endDate={endDate} items={[]} loadingMessage="Loading content sets..." />
  }
  if (familyContext.family === "content-set" && contentSetsStatus === ERROR) {
    return <MultiLineGraph status={ERROR} startDate={startDate} endDate={endDate} items={[]} errorMessage="Failed to load content sets. Please try again." />
  }

  if (familyContext.family === "performance") {
    return <CustomPerformanceGraph modelMetadata={modelMetadata} currentItems={currentItems} emptyMessage={emptyMessage} />
  }

  return <CustomBatchTrendGraph
    modelMetadata={modelMetadata}
    familyContext={familyContext}
    currentItems={currentItems}
    contentSetMetadata={contentSetMetadata}
    emptyMessage={emptyMessage}
  />
}
